import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { formatMinorAmount, getMinorUnitsSync } from '@sharecost/shared';
import type { CategorySlice } from '@/api/statistics';
import { useChartTheme } from '@/hooks/useChartTheme';
import { useTranslation } from 'react-i18next';

interface CategoryBreakdownChartProps {
  data: CategorySlice[];
  currency: string;
  totalSpent: number;
}

function CustomTooltip({ active, payload, currency }: any) {
  if (!active || !payload?.length) return null;
  const entry = payload[0].payload as CategorySlice;
  const minorUnits = getMinorUnitsSync(currency);
  return (
    <div className="rounded-lg bg-surface-primary px-3 py-2 shadow-lg ring-1 ring-border-primary">
      <p className="text-sm font-semibold text-text-primary">{entry.name}</p>
      <p className="text-sm text-text-secondary">
        {formatMinorAmount(entry.amount, currency, minorUnits)} ({entry.count} expense{entry.count !== 1 ? 's' : ''})
      </p>
    </div>
  );
}

export function CategoryBreakdownChart({ data, currency, totalSpent }: CategoryBreakdownChartProps) {
  const { t } = useTranslation();
  const minorUnits = getMinorUnitsSync(currency);
  const ct = useChartTheme();

  if (data.length === 0) {
    return (
      <div className="flex h-[280px] items-center justify-center text-sm text-text-quaternary">
        {t('groups:stats.noCategoryData')}
      </div>
    );
  }

  return (
    <div className="rounded-xl bg-surface-primary p-5 shadow-sm ring-1 ring-border-primary">
      <h3 className="mb-4 text-sm font-semibold text-text-primary">{t('web:categoryBreakdownChart.byCategory')}</h3>
      <div className="flex flex-col items-center gap-4 lg:flex-row lg:items-start">
        {/* Chart */}
        <div className="relative h-[220px] w-[220px] shrink-0">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={data}
                dataKey="amount"
                nameKey="name"
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={95}
                strokeWidth={2}
                stroke={ct.pieSeparator}
                activeIndex={-1}
                style={{ outline: 'none', cursor: 'default' }}
              >
                {data.map((entry, idx) => (
                  <Cell key={entry.categoryId ?? idx} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip currency={currency} />} />
            </PieChart>
          </ResponsiveContainer>
          <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
            <p className="text-xs text-text-tertiary">{t('groups:budgetProgress.total')}</p>
            <p className="text-sm font-bold text-text-primary">
              {formatMinorAmount(totalSpent, currency, minorUnits, undefined, true)}
            </p>
          </div>
        </div>

        {/* Legend */}
        <div className="flex-1 space-y-1.5">
          {data.map((entry, idx) => (
            <div key={entry.categoryId ?? idx} className="flex items-center gap-2 text-sm">
              <span
                className="inline-block h-3 w-3 shrink-0 rounded-sm"
                style={{ backgroundColor: entry.color }}
              />
              <span className="flex-1 truncate text-text-secondary">{entry.name}</span>
              <span className="shrink-0 text-text-quaternary">{entry.count}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
